import React, { useState, useEffect, useCallback } from 'react';
import { PaymentLinkAPI, ApprovalAPI, ProcessorAPI, SettingsAPI } from '../services/api';
import { PendingPaymentLink, PendingSelfAuth, UnifiedTransaction } from '../types/payment';
import { AlertService } from '../services/AlertService';
import { LoadingScreen } from './LoadingScreen';
import { getRoleDisplayName } from '../utils/rolePermissions';

interface Props {
  highlightId?: string | null;
  // Opens the shared TransactionDetailsPanel (lives on the Approvals page) with
  // an adapted view of the pending row.
  onViewDetails?: (tx: UnifiedTransaction) => void;
}

// One unified row, discriminated by kind.
type Row =
  | { kind: 'link'; id: string; createdAt: string; data: PendingPaymentLink }
  | { kind: 'self_auth'; id: string; createdAt: string; data: PendingSelfAuth };

/**
 * Unified pending-approvals view: payment-link requests (tag 3DS) and Self
 * Authorization requests (tag 3RI). Neither charges the customer before
 * approval — link rows send the link on approve; self-auth rows run the sale.
 */
export const PendingApprovalsTab: React.FC<Props> = ({ highlightId, onViewDetails }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);

  // Per-link 3DS toggle state (link rows only).
  const [threeDsById, setThreeDsById] = useState<Record<string, boolean>>({});

  const [approveLink, setApproveLink] = useState<PendingPaymentLink | null>(null);
  const [approveSelfAuth, setApproveSelfAuth] = useState<PendingSelfAuth | null>(null);
  const [rejectRow, setRejectRow] = useState<Row | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Per-category processor override for the open approve-link modal.
  const [processors, setProcessors] = useState<Array<{ id: string; name: string }>>([]);
  const [linkOverrides, setLinkOverrides] = useState<{ visa: string; mastercard: string; other: string }>({ visa: '', mastercard: '', other: '' });
  // The agent's routed processor per category — shown next to "Inherit" so the
  // approver sees which processor the default would use.
  const [agentDefaults, setAgentDefaults] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [linkRes, selfRes] = await Promise.all([
        PaymentLinkAPI.getPendingApproval().catch(() => ({ links: [], total_count: 0 })),
        ApprovalAPI.getPendingSelfAuth().catch(() => ({ items: [], total_count: 0 })),
      ]);

      const linkRows: Row[] = linkRes.links.map(l => ({ kind: 'link' as const, id: l.id, createdAt: l.created_at, data: l }));
      const selfRows: Row[] = selfRes.items.map(s => ({ kind: 'self_auth' as const, id: s.id, createdAt: s.created_at, data: s }));
      const merged = [...linkRows, ...selfRows].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setRows(merged);

      setThreeDsById(prev => {
        const next = { ...prev };
        for (const l of linkRes.links) {
          if (next[l.id] === undefined) next[l.id] = l.three_ds_required;
        }
        return next;
      });
    } catch (error: any) {
      console.error('Failed to load pending approvals:', error);
      AlertService.error(error.message || 'Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Processor options for the override dropdowns (loaded once).
  useEffect(() => {
    ProcessorAPI.getProcessors().then(setProcessors).catch(() => {});
  }, []);

  // QuickClick on: one processor per link, starting on the agent's assigned one.
  // The per-card overrides below don't apply there (the card isn't known yet).
  const [qcOptions, setQcOptions] = useState<{ account: string; name: string }[]>([]);
  const [qcChoice, setQcChoice] = useState('');
  useEffect(() => {
    Promise.all([SettingsAPI.getProcessorSettings(), PaymentLinkAPI.getQuickclickAccounts()])
      .then(([settings, options]) => setQcOptions(settings?.useQuickclick ? options : []))
      .catch(() => setQcOptions([]));
  }, []);
  const useQc = qcOptions.length > 0;

  const toggle3ds = (id: string) => setThreeDsById(prev => ({ ...prev, [id]: !prev[id] }));

  // Open the approve-link modal and load the agent's routing to show as the
  // per-category defaults (the approver can override each below).
  const openApproveLink = (link: PendingPaymentLink) => {
    setApproveLink(link);
    setLinkOverrides({ visa: '', mastercard: '', other: '' });
    setQcChoice(link.quickclick_account || '');
    setAgentDefaults({});
    // Load the agent's routing so "Inherit" can show the resolved processor name.
    SettingsAPI.getAgentRouting(link.created_by)
      .then((r: any) => {
        const map: Record<string, string> = {};
        (r?.routings || []).forEach((e: any) => {
          const cat = (e.cardCategory || e.card_category || '').toLowerCase();
          if (cat === 'visa' || cat === 'mastercard' || cat === 'other') {
            map[cat] = e.processorName || e.processor_name || '';
          }
        });
        setAgentDefaults(map);
      })
      .catch(() => setAgentDefaults({}));
  };

  const doApproveLink = async (link: PendingPaymentLink, deliveryMethod: 'email' | 'sms' | 'both') => {
    try {
      setProcessingId(link.id);
      await PaymentLinkAPI.approve(link.id, {
        three_ds_required: !!threeDsById[link.id],
        delivery_method: deliveryMethod,
        processor_visa_id: linkOverrides.visa || null,
        processor_mastercard_id: linkOverrides.mastercard || null,
        processor_other_id: linkOverrides.other || null,
        ...(useQc ? { quickclick_account: qcChoice || null } : {}),
      });
      const sentVia = deliveryMethod === 'both' ? 'Email & SMS' : deliveryMethod === 'sms' ? 'SMS' : 'email';
      AlertService.success(`Payment link approved and sent via ${sentVia}`);
      setApproveLink(null);
      load();
    } catch (error: any) {
      AlertService.error(error.message || 'Failed to approve payment link');
    } finally {
      setProcessingId(null);
    }
  };

  const doApproveSelfAuth = async (sale: PendingSelfAuth) => {
    try {
      setProcessingId(sale.id);
      const result = await ApprovalAPI.approveSale(sale.id, {});
      if (result.response_code === '100') {
        AlertService.success('Self-authorization approved — sale processed successfully');
        setApproveSelfAuth(null);
        load();
      } else {
        AlertService.error(`Sale failed: ${result.responsetext || 'declined'}`);
      }
    } catch (error: any) {
      AlertService.error(error.message || 'Failed to process the sale');
    } finally {
      setProcessingId(null);
    }
  };

  const doReject = async (row: Row) => {
    try {
      setProcessingId(row.id);
      if (row.kind === 'link') {
        await PaymentLinkAPI.reject(row.id, rejectReason);
      } else {
        await ApprovalAPI.rejectSale(row.id, { reason: rejectReason });
      }
      AlertService.success('Request rejected');
      setRejectRow(null);
      setRejectReason('');
      load();
    } catch (error: any) {
      AlertService.error(error.message || 'Failed to reject');
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return <LoadingScreen isVisible={true} message="Loading Pending Approvals..." />;
  }

  if (rows.length === 0) {
    return (
      <div className="empty-state">
        <h3>No Pending Approvals</h3>
        <p>There are no payment-link or self-authorization requests awaiting approval.</p>
        <button className="btn btn-outline-primary" onClick={load} disabled={loading} style={{ marginTop: '1rem' }}>
          {loading ? 'Loading...' : '🔄 Refresh'}
        </button>
      </div>
    );
  }

  const customerName = (d: { first_name?: string; last_name?: string }) =>
    `${d.first_name || ''} ${d.last_name || ''}`.trim() || '—';

  const formatDelivery = (m?: string) =>
    m === 'sms' ? '💬 SMS' : m === 'both' ? '✉️💬 Both' : m === 'email' ? '✉️ Email' : '—';

  // Adapt a pending row into the shape the shared TransactionDetailsPanel reads.
  // These requests aren't charged yet, so card/NMI/void sections stay empty.
  const rowToTransaction = (row: Row): UnifiedTransaction => {
    const d = row.data;
    const tx: any = {
      id: row.id,
      tx_type: 'sale',
      amount: d.amount,
      currency: d.currency,
      status: 'PENDING',
      created_at: row.createdAt,
      first_name: d.first_name,
      last_name: d.last_name,
      email: d.email,
      phone: d.phone,
      category: d.category,
      sale_state: d.sale_state,
      last4_ssn: (d as any).last4_ssn,
      dl_last4: (d as any).dl_last4,
      drivers_license_state: (d as any).drivers_license_state,
      dl_issuing_date: (d as any).dl_issuing_date,
      dl_expiration_date: (d as any).dl_expiration_date,
      created_by: d.created_by,
      created_by_name: d.created_by_name || d.created_by,
    };
    if (row.kind === 'link') {
      tx.order_description = row.data.order_description;
      tx.date_of_birth = row.data.date_of_birth;
      tx.delivery_method = row.data.delivery_method;
    } else {
      tx.last4 = row.data.last4;
      tx.card_brand = row.data.card_brand;
      tx.three_ds_status = row.data.three_ds_status;
    }
    return tx as UnifiedTransaction;
  };

  return (
    <div className="payments-table-container">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
        <button className="btn btn-outline-primary" onClick={load} disabled={loading}>
          {loading ? 'Loading...' : '🔄 Refresh'}
        </button>
      </div>
      <div className="table-wrapper">
        <table className="payments-table">
          <thead>
            <tr>
              <th>Created By</th>
              <th>Type</th>
              <th>Sale Type</th>
              <th>Customer</th>
              <th>DOB</th>
              <th>Amount</th>
              <th>Preferred</th>
              <th>3DS</th>
              <th>Date &amp; Time</th>
              <th className="actions-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const d = row.data;
              const busy = processingId === row.id;
              const isHighlighted = highlightId && row.id === highlightId;
              return (
                <tr key={`${row.kind}-${row.id}`} className={isHighlighted ? 'row-highlight' : ''}>
                  <td className="created-by-cell">
                    {d.created_by_name || d.created_by}
                    {d.creator_role && <div className="creator-role">{getRoleDisplayName(d.creator_role)}</div>}
                  </td>
                  <td>
                    <span className={`kind-tag ${row.kind === 'link' ? 'kind-3ds' : 'kind-3ri'}`}>
                      {row.kind === 'link' ? '3DS' : '3RI'}
                    </span>
                  </td>
                  <td><span className="sale-type-badge">{d.category || 'N/A'}</span></td>
                  <td>{customerName(d)}</td>
                  <td className="dob-cell">{row.kind === 'link' ? (row.data.date_of_birth || '—') : '—'}</td>
                  <td className="amount-cell"><span className="amount-value">${Number(d.amount).toFixed(2)}</span></td>
                  <td className="preferred-cell">{row.kind === 'link' ? formatDelivery(row.data.delivery_method) : '—'}</td>
                  <td>
                    {row.kind === 'link' ? (
                      <label className="threeds-toggle" title="Require 3DS for this transaction">
                        <input
                          type="checkbox"
                          checked={!!threeDsById[row.id]}
                          onChange={() => toggle3ds(row.id)}
                          disabled={!row.data.can_approve || busy}
                        />
                        <span className="threeds-slider" />
                        <span className="threeds-label">{threeDsById[row.id] ? 'ON' : 'OFF'}</span>
                      </label>
                    ) : (
                      <span className="threeds-label" title="3RI authentication already completed">
                        {(row.data.three_ds_status || '').startsWith('Y') ? '✓ 3RI' : '—'}
                      </span>
                    )}
                  </td>
                  <td className="date-cell">
                    {new Date(row.createdAt).toLocaleDateString()}<br />
                    <small>{new Date(row.createdAt).toLocaleTimeString()}</small>
                  </td>
                  <td className="actions-cell">
                    <div className="action-buttons">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => onViewDetails?.(rowToTransaction(row))}
                        title="View details"
                      >
                        👁 Details
                      </button>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => row.kind === 'link' ? openApproveLink(row.data) : setApproveSelfAuth(row.data)}
                        disabled={!d.can_approve || busy}
                        title={d.can_approve ? 'Approve' : 'You cannot approve this request'}
                      >
                        {busy ? '⏳' : '✅'} Approve
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => { setRejectRow(row); setRejectReason(''); }}
                        disabled={!d.can_approve || busy}
                        title="Reject"
                      >
                        🚫 Reject
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Approve payment link — choose channel */}
      {approveLink && (
        <div className="modal-overlay" onClick={() => setApproveLink(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Send Payment Link</h2>
            <div className="approval-summary">
              <p><strong>Customer:</strong> {customerName(approveLink)}</p>
              <p><strong>Amount:</strong> ${Number(approveLink.amount).toFixed(2)}</p>
              <p><strong>3DS:</strong> {threeDsById[approveLink.id] ? 'Enabled' : 'Disabled'} for this transaction</p>
              <p><strong>Agent preferred:</strong> {formatDelivery(approveLink.delivery_method)}</p>
            </div>

            {useQc && (
              <div className="processor-override">
                <label className="choose-channel-label" htmlFor="qcProcessor">Processor</label>
                <small style={{ display: 'block', marginBottom: '0.5rem', color: '#6b7280' }}>
                  Starts on the agent's assigned processor. The customer pays through the one selected here.
                </small>
                <select
                  id="qcProcessor"
                  value={qcChoice}
                  onChange={e => setQcChoice(e.target.value)}
                  style={{ width: '100%', padding: '0.4rem', marginBottom: '0.75rem' }}
                >
                  <option value="">{`Default (${qcOptions[0].name})`}</option>
                  {qcOptions.map(o => <option key={o.account} value={o.account}>{o.name}</option>)}
                </select>
              </div>
            )}

            {/* Per-category processor override. Blank = inherit the agent's routing. */}
            {!useQc && <div className="processor-override">
              <p className="choose-channel-label">Processor routing (optional override)</p>
              <small style={{ display: 'block', marginBottom: '0.5rem', color: '#6b7280' }}>
                Leave as “Inherit” to use the agent's routing (the default). Overriding forces the chosen processor for that card type on this link.
              </small>
              {(['visa', 'mastercard', 'other'] as const).map(cat => (
                <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                  <label style={{ width: 100 }}>
                    {cat === 'visa' ? 'Visa' : cat === 'mastercard' ? 'Mastercard' : 'Other'}
                  </label>
                  <select
                    value={linkOverrides[cat]}
                    onChange={e => setLinkOverrides(o => ({ ...o, [cat]: e.target.value }))}
                    style={{ flex: 1, padding: '0.4rem' }}
                  >
                    <option value="">{agentDefaults[cat] ? `Inherit (${agentDefaults[cat]})` : 'Inherit'}</option>
                    {processors.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>}

            <p className="choose-channel-label">How should this link be delivered to the customer?</p>
            <div className="channel-buttons">
              {/* Only offer channels the link actually has a destination for. */}
              {approveLink.has_email && (
                <button
                  className={`btn btn-primary ${approveLink.delivery_method === 'email' ? 'channel-preferred' : ''}`}
                  disabled={processingId === approveLink.id}
                  title="Send via email"
                  onClick={() => doApproveLink(approveLink, 'email')}
                >
                  ✉️ Send Email{approveLink.delivery_method === 'email' ? ' ★' : ''}
                </button>
              )}
              {approveLink.has_phone && (
                <button
                  className={`btn btn-primary ${approveLink.delivery_method === 'sms' ? 'channel-preferred' : ''}`}
                  disabled={processingId === approveLink.id}
                  title="Send via SMS"
                  onClick={() => doApproveLink(approveLink, 'sms')}
                >
                  💬 Send SMS{approveLink.delivery_method === 'sms' ? ' ★' : ''}
                </button>
              )}
              {approveLink.has_email && approveLink.has_phone && (
                <button
                  className={`btn btn-primary ${approveLink.delivery_method === 'both' ? 'channel-preferred' : ''}`}
                  disabled={processingId === approveLink.id}
                  title="Send via email and SMS"
                  onClick={() => doApproveLink(approveLink, 'both')}
                >
                  ✉️💬 Send Both{approveLink.delivery_method === 'both' ? ' ★' : ''}
                </button>
              )}
            </div>
            <div className="form-actions">
              <button className="btn btn-outline-secondary" onClick={() => setApproveLink(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Approve self-authorization — confirm charge */}
      {approveSelfAuth && (
        <div className="modal-overlay" onClick={() => setApproveSelfAuth(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Approve &amp; Charge</h2>
            <div className="approval-summary">
              <p><strong>Customer:</strong> {customerName(approveSelfAuth)}</p>
              <p><strong>Amount:</strong> ${Number(approveSelfAuth.amount).toFixed(2)}</p>
              {approveSelfAuth.last4 && <p><strong>Card:</strong> {approveSelfAuth.card_brand || 'Card'} •••• {approveSelfAuth.last4}</p>}
            </div>
            <p className="choose-channel-label">
              This runs the sale and charges the customer's card now. The card was already 3RI-authenticated. Proceed?
            </p>
            <div className="form-actions">
              <button className="btn btn-outline-secondary" onClick={() => setApproveSelfAuth(null)}>Cancel</button>
              <button
                className="btn btn-success"
                disabled={processingId === approveSelfAuth.id}
                onClick={() => doApproveSelfAuth(approveSelfAuth)}
              >
                {processingId === approveSelfAuth.id ? 'Processing...' : 'Approve & Charge'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject (both kinds) */}
      {rejectRow && (
        <div className="modal-overlay" onClick={() => setRejectRow(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Reject {rejectRow.kind === 'link' ? 'Payment Link' : 'Self-Authorization'}</h2>
            <div className="rejection-summary">
              <p><strong>Customer:</strong> {customerName(rejectRow.data)}</p>
              <p><strong>Amount:</strong> ${Number(rejectRow.data.amount).toFixed(2)}</p>
            </div>
            <div className="form-group">
              <label>Rejection Reason*</label>
              <textarea
                rows={4}
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="Please provide a reason for rejecting this request..."
              />
            </div>
            <div className="form-actions">
              <button className="btn btn-outline-secondary" onClick={() => setRejectRow(null)}>Cancel</button>
              <button
                className="btn btn-danger"
                disabled={!rejectReason.trim() || processingId === rejectRow.id}
                onClick={() => doReject(rejectRow)}
              >
                {processingId === rejectRow.id ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .creator-role { font-size: 0.7rem; color: var(--text-tertiary); text-transform: uppercase; }
        .row-highlight { background: var(--warning-light, rgba(245, 158, 11, 0.12)) !important; }
        .kind-tag {
          display: inline-block;
          padding: 0.2rem 0.5rem;
          border-radius: 6px;
          font-size: 0.7rem;
          font-weight: 700;
          white-space: nowrap;
        }
        .kind-3ds { background: rgba(37, 99, 235, 0.12); color: #2563eb; border: 1px solid rgba(37, 99, 235, 0.3); }
        .kind-3ri { background: rgba(16, 185, 129, 0.12); color: #059669; border: 1px solid rgba(16, 185, 129, 0.3); }
        .threeds-toggle { display: inline-flex; align-items: center; gap: 0.4rem; cursor: pointer; user-select: none; }
        .threeds-toggle input { display: none; }
        .threeds-slider {
          width: 34px; height: 18px; border-radius: 9px;
          background: var(--border-primary, #cbd5e1); position: relative; transition: background 0.2s; flex: none;
        }
        .threeds-slider::after {
          content: ''; position: absolute; top: 2px; left: 2px; width: 14px; height: 14px;
          border-radius: 50%; background: #fff; transition: transform 0.2s;
        }
        .threeds-toggle input:checked + .threeds-slider { background: var(--success, #059669); }
        .threeds-toggle input:checked + .threeds-slider::after { transform: translateX(16px); }
        .threeds-toggle input:disabled + .threeds-slider { opacity: 0.5; cursor: not-allowed; }
        .threeds-label { font-size: 0.7rem; font-weight: 700; color: var(--text-secondary); min-width: 24px; }
        .choose-channel-label { margin: 0 0 1rem 0; color: var(--text-primary); font-weight: 500; }
        .channel-buttons { display: flex; gap: 1rem; margin-bottom: 1rem; }
        .channel-buttons .btn { flex: 1; }
        .channel-buttons .channel-preferred { box-shadow: 0 0 0 2px var(--success, #059669); }
        .dob-cell, .preferred-cell { white-space: nowrap; font-size: 0.85rem; }
      `}</style>
    </div>
  );
};
